// src/components/LiquidGlass.js

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import React from 'react';

let filterCounter = 0;

/**
 * Pure-CSS "liquid glass" wrapper, with mobile-only effect and desktop fallback.
 * Gracefully falls back to a simple frosted overlay if backdrop-filter isn't supported.
 *
 * Props:
 * - dispScale: intensity of distortion
 * - blur: backdrop blur amount
 * - radius: default border-radius of glass
 * - frostOpacity: white overlay opacity
 * - frosted: true = skip turbulence, use blur-only frost
 * - icon: CSS class for an <i> icon in header on mobile
 * - title: node/text for header title on mobile
 * - altComponent: React element/type for desktop fallback
 * - altProps: props to forward to altComponent
 * - altSx: sx overrides for altComponent
 * - sx: sx for the mobile wrapper (can include padding, margin, etc.)
 */
const LiquidGlass = ({
  dispScale = 200,
  blur = 4,
  radius = '8px',
  frostOpacity = 0.08,
  frosted = true,
  icon,
  title,
  altComponent = null,
  altProps = {},
  altSx = {},
  sx = {},
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Stable ref for SVG filter ID
  const filterIdRef = React.useRef(null);
  if (filterIdRef.current === null) {
    filterIdRef.current = `liquidGlassFilter${++filterCounter}`;
  }
  const filterId = filterIdRef.current;

  // Desktop fallback
  if (!isMobile) {
    if (altComponent) {
      if (React.isValidElement(altComponent)) {
        const props = {
          ...altComponent.props,
          ...altProps,
          sx: { ...(altComponent.props.sx || {}), ...altSx },
        };
        return React.cloneElement(altComponent, props, children);
      }
      return React.createElement(altComponent, { ...altProps, sx: altSx }, children);
    }
    return <>{children}</>;
  }

  // Mobile: separate out borderRadius to override default, keep everything else (including padding)
  const {
    borderRadius: sxBorderRadius,
    background,
    backgroundColor,
    backdropFilter,
    filter,
    ...restSx
  } = sx;
  const effectiveRadius = sxBorderRadius || radius;

  // Feature-detect backdrop-filter support
  const canBackdrop =
    typeof CSS !== 'undefined' &&
    (CSS.supports('backdrop-filter', `blur(${blur}px)`) ||
     CSS.supports('-webkit-backdrop-filter', `blur(${blur}px)`));

  // Use full liquid effect only if not frosted and backdrop-filter is supported
  const useLiquid = !frosted && canBackdrop;

  return (
    <>
      {/* SVG turbulence filter */}
      {useLiquid && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          style={{ position: 'absolute', width: 0, height: 0 }}
          aria-hidden="true"
        >
          <filter
            id={filterId}
            x="0" y="0" width="100%" height="100%"
            filterUnits="objectBoundingBox"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.0025 0.0025"
              numOctaves="3"
              result="noise"
            />
            <feGaussianBlur in="noise" stdDeviation="1.5" result="blurredNoise" />
            <feDisplacementMap
              in="SourceGraphic"
              in2="blurredNoise"
              scale={dispScale}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </svg>
      )}

      {/* Outer wrapper */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: effectiveRadius,
          width: '100%',
          height: '100%',
          ...restSx, // includes any padding, margin etc.
        }}
      >
        {/* Distortion / blur layer */}
        <Box
          className="bend"
          sx={{
            position: 'absolute',
            inset: 0,
            ...(canBackdrop && {
              backdropFilter: `blur(${blur}px) saturate(200%) brightness(115%)`,
              WebkitBackdropFilter: `blur(${blur}px) saturate(200%) brightness(115%)`,
            }),
            ...(useLiquid && { filter: `url(#${filterId})` }),
            borderRadius: effectiveRadius,
            zIndex: 0,
          }}
        />

        {/* Frost overlay */}
        <Box
          className="frost"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: `rgba(255,255,255,${frostOpacity})`,
            borderRadius: effectiveRadius,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />

        {/* Face highlight */}
        <Box
          className="face"
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: effectiveRadius,
            boxShadow: '0 6px 6px rgba(0,0,0,0.2), 0 0 16px rgba(0,0,0,0.12)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />

        {/* Inner edge */}
        <Box
          className="edge"
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: effectiveRadius,
            boxShadow:
              'inset 1px 1px 1px rgba(255,255,255,0.1), inset -1px -1px 1px rgba(255,255,255,0.1)',
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />

        {/* Header + content */}
        <Box sx={{ position: 'relative', zIndex: 4 }}>
          {(icon || title) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                mb: 1,
                backgroundColor: 'rgba(255,255,255,0.15)',
                ...(canBackdrop && { backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }),
                color: '#fff',
                borderTopLeftRadius: effectiveRadius,
                borderTopRightRadius: effectiveRadius,
              }}
            >
              {icon && (
                <Box component="i" className={icon} sx={{ mr: 1, fontSize: '1rem' }} />
              )}
              {title && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  {title}
                </Typography>
              )}
            </Box>
          )}
          {children}
        </Box>
      </Box>
    </>
  );
};

LiquidGlass.propTypes = {
  dispScale: PropTypes.number,
  blur: PropTypes.number,
  radius: PropTypes.string,
  frostOpacity: PropTypes.number,
  frosted: PropTypes.bool,
  icon: PropTypes.string,
  title: PropTypes.node,
  altComponent: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.elementType,
  ]),
  altProps: PropTypes.object,
  altSx: PropTypes.object,
  sx: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export default LiquidGlass;